/* eslint-disable @typescript-eslint/no-unused-vars */
import { Roles } from "@/utilities/types/Users.ts";
import { HeaderCell } from "@/components/ui/table.tsx";
import useProfile from "@/hooks/use-profile.ts";
import DeletePopover from "@/app/shared/delete-popover.tsx";
import { ActionIcon, Button, Tooltip } from "rizzui";
import CellEdit from "@/components/table-cell-custom/cell-edit";
import { IUnit, IUnitDTO, UnitStatus } from "@/utilities/types/Units";
import { useState } from "react";
import CustomPopover from "@/app/shared/custom-popover";
import { BiCheck } from "react-icons/bi";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { unitSchema } from "@/utilities/validators/units";
import { SortConfig } from "@/hooks/useTableServer";

type UnitsSortBy = "name" | "price" | "status";

const statusOptions: { label: string; value: UnitStatus }[] = [
  {
    label: "Available",
    value: UnitStatus.AVAILABLE,
  },
  {
    label: "Sold",
    value: UnitStatus.SOLD,
  },

  {
    label: "Not Released",
    value: UnitStatus.NOT_RELEASED,
  },
  {
    label: "Reserved",
    value: UnitStatus.RESERVED,
  },
];

type Columns = {
  data: IUnit[];
  sortConfig?: SortConfig<UnitsSortBy>;
  onDeleteItem?: (id: string) => void;
  onHeaderCellClick: (value: string) => void;
  onUpdateFloor?: (id: string) => Promise<void>;
  reset?: () => void;
  role: Roles;
  projectId: string;
  onUpdateItem: (
    id: string,
    data: Partial<IUnitDTO>
  ) => Promise<void> | undefined;
  isAdding: boolean;
  onAddUnit: (data: IUnitDTO) => Promise<void>;
};
export const useUnitsColumns = ({
  data,
  sortConfig,
  onHeaderCellClick,
  onDeleteItem,
  role,
  reset,
  projectId,
  onUpdateItem,
  isAdding,
  onAddUnit,
}: Columns) => {
  const [addData, setAddData] = useState<IUnitDTO>({
    name: "",
    price: "",
    status: "available",
  });

  const form = useForm<IUnitDTO>({
    defaultValues: addData,
    mode: "onChange",
    resolver: zodResolver(unitSchema),
  });

  const onSubmit = async (values: IUnitDTO) => {
    await onAddUnit(values);
    form.reset({
      name: "",
      price: "",
      status: "available",
    });
    setAddData({
      name: "",
      price: "",
      status: "available",
    });
  };

  const { profile } = useProfile();

  return [
    {
      title: (
        <HeaderCell
          title="Name"
          align="left"
          sortable
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "name"
          }
        />
      ),
      onHeaderCell: () => onHeaderCellClick("name"),
      dataIndex: "name",
      key: "name",

      render: (name: string, row: IUnit & { isAdd?: boolean }) => {
        return (
          <Controller
            name="name"
            control={form.control}
            render={({ field, fieldState }) => {
              const rawValue = name?.startsWith("#") ? name.slice(1) : name;
              return (
                <CellEdit
                  defaultValue={rawValue}
                  prefix="#"
                  onSubmit={async (val) => {
                    const value = `#${val}`;
                    if (row?.isAdd) {
                      setAddData({
                        ...addData,
                        name: value,
                      });
                      return;
                    }
                    await onUpdateItem(row.id, {
                      name: value,
                    });
                  }}
                  onChange={field.onChange}
                  defaultToggle={row.isAdd}
                  error={fieldState?.error?.message || ""}
                  readOnly={profile?.role !== Roles.ADMIN}
                />
              );
            }}
          />
        );
      },
    },

    {
      title: (
        <HeaderCell
          title="Price"
          sortable
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "price"
          }
        />
      ),
      onHeaderCell: () => onHeaderCellClick("price"),
      dataIndex: "price",
      key: "price",

      render: (price: number, row: IUnit & { isAdd?: boolean }) => {
        return (
          <Controller
            name="price"
            control={form.control}
            render={({ field, fieldState }) => {
              return (
                <CellEdit
                  defaultValue={price?.toString() || "0"}
                  onSubmit={async (value) => {
                    if (row?.isAdd) {
                      setAddData({
                        ...addData,
                        price: value,
                      });
                      return;
                    }
                    await onUpdateItem(row.id, {
                      price: value,
                    });
                  }}
                  onChange={field.onChange}
                  defaultToggle={row.isAdd}
                  error={fieldState?.error?.message || ""}
                  readOnly={profile?.role !== Roles.ADMIN}
                  isNumber={true}
                />
              );
            }}
          />
        );
      },
    },
    {
      title: (
        <HeaderCell
          title="Status"
          align="center"
          ascending={
            sortConfig?.direction === "asc" && sortConfig?.key === "status"
          }
        />
      ),
      onHeaderCell: () => onHeaderCellClick("status"),
      dataIndex: "status",
      key: "status",
      render: (status: UnitStatus, row: IUnit & { isAdd?: boolean }) => {
        const optionValue = row?.isAdd ? addData.status : status;
        return (
          <div className="flex justify-center items-center gap-1">
            <Controller
              name="status"
              control={form.control}
              render={({ field }) => {
                return (
                  <>
                    {statusOptions.map((option) => {
                      const isSelected = option.value === optionValue;
                      return (
                        <Button
                          key={option.value}
                          size="sm"
                          variant={isSelected ? "solid" : "outline"}
                          className={`px-2 py-1 text-xs capitalize ${
                            isSelected
                              ? "bg-gray-700 text-white"
                              : "text-gray-600"
                          }`}
                          disabled={profile?.role !== Roles.ADMIN}
                          onClick={async () => {
                            if (row?.isAdd) {
                              setAddData({ ...addData, status: option.value });
                              field.onChange(option.value);
                              return;
                            }
                            await onUpdateItem(row.id, { status: option.value });
                          }}
                        >
                          {option.label}
                        </Button>
                      );
                    })}
                  </>
                );
              }}
            />
          </div>
        );
      },
    },

    {
      title: (
        <HeaderCell
          className="ps-3"
          title={<span className="whitespace-nowrap">Actions</span>}
        />
      ),
      dataIndex: "action",
      key: "action",
      hidden: profile?.role !== Roles.ADMIN,
      width: 120,
      render: (_: string, row: IUnit & { isAdd?: boolean }) => {
        if (role === Roles.ADMIN) {
          return (
            <div className="flex flex-row items-center justify-center">
              <ActionAdmin
                onDeleteItem={() => onDeleteItem?.(row.id)}
                row={row}
                projectId={projectId}
                reset={reset}
                isAdding={!!row?.isAdd}
              />
              {!!row?.isAdd && (
                <CustomPopover
                  title={`Add Unit`}
                  description={`Are you sure to add this unit?`}
                  onAccept={async () => {
                    form.handleSubmit(onSubmit)();
                  }}
                >
                  <Tooltip
                    size="sm"
                    content={"Submit"}
                    placement="top"
                    color="invert"
                  >
                    <ActionIcon size="sm" variant="text">
                      <BiCheck className="h-4 w-4" />
                    </ActionIcon>
                  </Tooltip>
                </CustomPopover>
              )}
            </div>
          );
        }
      },
    },
  ];
};
// eslint-disable-next-line react-refresh/only-export-components
const ActionAdmin = ({
  onDeleteItem,
  row,
  projectId,
  reset,
  isAdding,
  onAdd,
}: {
  onDeleteItem: () => void;
  reset?: () => void;
  onAdd?: () => Promise<void>;
  row: IUnit;
  projectId: string;
  isAdding: boolean;
}) => {
  return (
    <div className="flex items-center justify-center gap-3 pe-4">
      <>
        <DeletePopover
          title={`Delete Unit`}
          description={`Are you sure to delete this unit`}
          onDelete={onDeleteItem}
        />
      </>
    </div>
  );
};
